import React from "react";
import clsx from "clsx";
import { useDispatch, useStore } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import type { GridCellParams } from "@material-ui/x-grid";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import XGrid from "src/components/CSVFormattedXGrid";
import {
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from "@material-ui/x-grid";

import Typography from "@material-ui/core/Typography";

import { formatCurrency } from "src/utils";
import { setActiveDialog } from "src/store/adminHtml/actions";

import type {
  PlatformTransactionReport as PlatformTransactionReportType,
  PlatformTransactionRow,
} from "./report";
import Title, { DownloadButton } from "./Title";
import CompanyInfo from "./CompanyInfo";
import { printReport } from "./pdf";
import { useOpenDialog } from "./useOpenDialog";
import StaleReportWarning from "./StaleReportWarning";

export interface PlatformTransactionReportProps {
  report: PlatformTransactionReportType;
  showStaleReportWarning?: boolean;
  onRerunReport?: () => void;
  onReportDataMutated?: () => void;
}

const useValueBoxStyles = makeStyles((theme) => ({
  box: {
    margin: theme.spacing(1),
    padding: theme.spacing(1),
    minWidth: "15%",
  },
  title: {
    color: theme.palette.text.primary,
  },
  children: {
    textAlign: "right",
  },
}));

function ValueBox({ title, children }) {
  const classes = useValueBoxStyles();
  return (
    <Paper className={classes.box} variant="outlined">
      <Typography
        variant="button"
        component="p"
        display="block"
        className={classes.title}
      >
        {title}
      </Typography>
      <Typography variant="body2" className={classes.children}>
        {new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(children)}
      </Typography>
    </Paper>
  );
}

const Columns = [
  {
    field: "date",
    headerName: "Date",
    type: "date",
    width: 100,
  },
  {
    field: "transactionId",
    headerName: "Platform ID",
    width: 200,
  },
  {
    field: "title",
    headerName: "Title/Description",
    flex: 1,
  },
  {
    field: "moneyIn",
    headerName: "Money In",
    valueFormatter: formatCurrency,
    type: "number",
    width: 115,
  },
  {
    field: "moneyOut",
    headerName: "Money Out",
    valueFormatter: formatCurrency,
    type: "number",
    width: 135,
  },
];

function Toolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarColumnsButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "0px 10px",
      fontSize: theme.typography.caption.fontSize,
    },
    "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-cell": {
      color: theme.palette.text.primary,
    },
    "& .MuiDataGrid-iconSeparator": {
      opacity: 0,
    },
    "& .MuiDataGrid-columnsContainer": {
      backgroundColor: theme.palette.action.hover,
      borderTop: `1px solid ${theme.palette.divider}`,
      fontSize: theme.typography.caption.fontSize,
    },
    "& .MuiDataGrid-colCell": {
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "0px 10px",
    },
    height: "auto",
  },
  printable: {
    "& .MuiDataGrid-windowContainer": {
      height: "auto !important",
    },
    "& .MuiDataGrid-window": {
      position: "unset !important",
      marginTop: "39px !important",
    },
    "& .MuiDataGrid-viewport": {
      maxHeight: "auto !important",
    },
    "& .MuiDataGrid-renderingZone": {
      maxHeight: "auto !important",
    },
  },
  padding: {
    padding: theme.spacing(2),
  },
  sectionTitle: {
    color: theme.palette.text.primary,
  },
}));

interface PlatformTransactionReportContentProps {
  report: PlatformTransactionReportType;
  actions?: React.ReactNode;
  printable?: boolean;
  showStaleReportWarning?: boolean;
  onRerunReport?: () => void;
  onReportDataMutated?: () => void;
}

function PlatformTransactionReportContent({
  report,
  actions,
  printable = false,
  showStaleReportWarning,
  onRerunReport,
  onReportDataMutated,
}: PlatformTransactionReportContentProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const openDialog = useOpenDialog();

  return (
    <div
      className={clsx(
        classes.root,
        printable ? classes.printable : classes.padding
      )}
    >
      {showStaleReportWarning ? (
        <StaleReportWarning onRerunClick={onRerunReport} />
      ) : null}
      <CompanyInfo forPrint={printable} />
      <Title actions={actions}>{report.data.reportDetails.titleFull}</Title>
      <Box display="flex" justifyContent="center" flexWrap="wrap">
        <ValueBox title="Sales">{report.data.totals.sales}</ValueBox>
        <ValueBox title="Refunds">{report.data.totals.returns}</ValueBox>
        <ValueBox title="Expenses">{report.data.totals.expenses}</ValueBox>
        <ValueBox title="Transfers">{report.data.totals.transfers}</ValueBox>
        <ValueBox title="Other">{report.data.totals.other}</ValueBox>
      </Box>
      <Box mt={2}>
        <h2 className={classes.sectionTitle}>Transaction Detail</h2>
        <Box minHeight={!printable ? "75vh" : undefined}>
          <XGrid
            components={!printable ? { Toolbar } : undefined}
            density="compact"
            rows={report.data.rows}
            columns={Columns}
            disableSelectionOnClick
            autoHeight
            pageSize={report.data.rows.length}
            hideFooter
            hideFooterPagination
            disableColumnMenu
            onCellDoubleClick={async (params: GridCellParams) => {
              const row = params.row as PlatformTransactionRow;

              if (row.journal_entry_id) {
                dispatch(
                  setActiveDialog("add_journal", {
                    id: row.journal_entry_id,
                  })
                );
                return;
              }

              if (row.collection === "transaction" && row.collectionId) {
                const res = await openDialog("transaction", row.collectionId);
                if (res.status === "updated") onReportDataMutated?.();
                return;
              }

              if (row.collection === "sale" && row.collectionId) {
                const res = await openDialog("sale", row.collectionId);
                if (res.status === "updated") onReportDataMutated?.();
              }
            }}
          />
        </Box>
      </Box>
    </div>
  );
}

function PlatformTransactionReport({
  report,
  showStaleReportWarning,
  onRerunReport,
  onReportDataMutated,
}: PlatformTransactionReportProps) {
  const store = useStore();
  const handleDownloadReport = async () => {
    const filename = report.data.reportDetails.filename;
    await printReport(
      <PlatformTransactionReportContent report={report} printable />,
      { store, title: filename }
    );
  };

  return (
    <Box mt={2}>
      <Paper>
        <PlatformTransactionReportContent
          report={report}
          actions={<DownloadButton onClick={handleDownloadReport} />}
          showStaleReportWarning={showStaleReportWarning}
          onRerunReport={onRerunReport}
          onReportDataMutated={onReportDataMutated}
        />
      </Paper>
    </Box>
  );
}

export default PlatformTransactionReport;
